import { type ComputedRef, computed, onBeforeUnmount, onMounted, type Ref, ref } from "vue";
import { BREAKPOINTS, type BreakpointName } from "@/breakpoints";

// Re-exported so callers reaching for a breakpoint-aware helper find the values alongside
// it; `@/breakpoints` remains the definition and Tailwind's `screens` derives from it too.
export { BREAKPOINTS, type BreakpointName };

/**
 * Reactively tracks whether a CSS media query currently matches.
 *
 * The listener is registered on mount and torn down on unmount, so this must be
 * called from within a component `setup()` (or another composable). When
 * `window.matchMedia` is unavailable (e.g. SSR) the ref simply stays `false`.
 *
 * @param query - A media query string, e.g. `"(max-width: 768px)"`.
 * @returns A readonly-style ref that is `true` while the query matches.
 */
export function useMediaQuery(query: string): Ref<boolean> {
  const matches = ref(false);
  let mediaQueryList: MediaQueryList | null = null;

  const update = (): void => {
    matches.value = mediaQueryList?.matches ?? false;
  };

  onMounted(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    mediaQueryList = window.matchMedia(query);
    update();
    mediaQueryList.addEventListener?.("change", update);
  });

  onBeforeUnmount(() => {
    if (!mediaQueryList) return;
    mediaQueryList.removeEventListener?.("change", update);
  });

  return matches;
}

/**
 * Reactively tracks whether the viewport is narrower than `name`'s breakpoint — i.e.
 * whether that breakpoint's Tailwind utilities are *not* applying.
 *
 * This is the signal to use for layout decisions (which container, which grid, how many
 * columns), because it asks about available width and nothing else. For questions about
 * input capability — touch targets, gestures, hover-dependent UI — use `isTouchDevice`
 * from {@link useMobile} instead.
 *
 * @param name - Breakpoint to compare against, e.g. `"md"`.
 * @returns A ref that is `true` while the viewport is below that breakpoint.
 */
export const useBreakpointDown = (name: BreakpointName): Ref<boolean> => {
  // `- 1` mirrors Tailwind's own boundary: `md:` applies from exactly 900px up, so "below
  // md" must stop at 899px. Without it both branches would be true at exactly 900px.
  return useMediaQuery(`(max-width: ${BREAKPOINTS[name] - 1}px)`);
};

/** Options accepted by {@link useMobile}. */
export interface UseMobileOptions {
  /**
   * Viewport width (px) at or below which the device is considered "narrow". Defaults to
   * 768. Only feeds the legacy `isMobile` heuristic — `isNarrowViewport` always tracks the
   * `md` breakpoint from {@link BREAKPOINTS}.
   */
  maxWidth?: number;
}

/** Return shape of {@link useMobile}. */
export interface UseMobileReturn {
  /** True when the user agent reports an Android device. */
  isAndroid: () => boolean;
  /** True when the user agent reports an iOS device. */
  isIOS: () => boolean;
  /**
   * Reactive flag combining user-agent sniffing with media-query/touch heuristics.
   *
   * Deliberately width-independent in part: a coarse pointer or a no-hover device reports
   * mobile at *any* width, so a landscape tablet at 1366px is `isMobile === true` while
   * every `md:`/`lg:` utility is still applying. Good for device affordances, wrong for
   * layout — see `isNarrowViewport`.
   */
  isMobile: ComputedRef<boolean>;
  /**
   * True while the viewport is below the `md` breakpoint (< 900px), regardless of device.
   * Use this to pick layouts so the choice cannot disagree with `md:` utility classes.
   */
  isNarrowViewport: Ref<boolean>;
  /**
   * True when the device is driven by touch (or otherwise cannot hover), regardless of
   * viewport width. Use this for touch targets, gestures, and hover-dependent UI.
   */
  isTouchDevice: ComputedRef<boolean>;
}

// Non-standard window globals used for broader device detection; typed locally
// so the detection logic stays strict without polluting the global lib types.
interface LegacyWindow extends Window {
  opera?: string;
  MSStream?: unknown;
}

/**
 * Helper to determine if the user is on a mobile device.
 *
 * Returns three signals answering three different questions — pick by what the caller
 * actually depends on:
 *
 * - `isNarrowViewport` — *"how much width do I have?"* Layout decisions: which container,
 *   grid, or column count. Tracks the `md` breakpoint, so it always agrees with `md:`.
 * - `isTouchDevice` — *"how is this being driven?"* Touch targets, gestures, hover UI.
 * - `isMobile` — the original combined heuristic (UA sniffing plus narrow-viewport, coarse
 *   pointer, and no-hover media queries). Kept as-is for existing callers; it conflates the
 *   two questions above, so prefer the specific signal in new code.
 */
export const useMobile = (options: UseMobileOptions = { maxWidth: 768 }): UseMobileReturn => {
  const maxWidth = options?.maxWidth ?? 768;
  const isNarrow = useMediaQuery(`(max-width: ${maxWidth}px)`);
  const isNarrowViewport = useBreakpointDown("md");
  const isCoarse = useMediaQuery("(pointer: coarse)");
  const noHover = useMediaQuery("(hover: none)");
  const legacyWindow = window as unknown as LegacyWindow;
  const hasTouchScreen = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const userAgent = navigator.userAgent || navigator.vendor || legacyWindow.opera || "";

  const hasMobileAttributes = computed<boolean>(
    () => (hasTouchScreen && isNarrow.value) || isCoarse.value || noHover.value,
  );

  const isAndroid = (): boolean => {
    return /android/i.test(userAgent);
  };

  const isIOS = (): boolean => {
    return /iPad|iPhone|iPod/.test(userAgent) && !legacyWindow.MSStream;
  };

  const isMobile = computed<boolean>(() => isAndroid() || isIOS() || hasMobileAttributes.value);

  // Capability only — no width term, unlike `hasMobileAttributes`. A touch laptop or a
  // landscape tablet is a touch device at any size, which is exactly what callers styling
  // touch targets or gestures need to know.
  const isTouchDevice = computed<boolean>(() => hasTouchScreen || isCoarse.value || noHover.value);

  return {
    isAndroid,
    isIOS,
    isMobile,
    isNarrowViewport,
    isTouchDevice,
  };
};
